import { inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  endBefore,
  limitToLast,
  getDocs,
  getCountFromServer,
  Query
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable, from, map, of } from 'rxjs';
import { Product } from '../models/product';
import { Filter } from '../models/filter';


export interface ProductPage {
  products: Product[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  firstDoc?: QueryDocumentSnapshot<DocumentData>;
}

export interface ProductFilters {
  gender?: string;
  productCategory?: string;
  designTheme?: string;
  color?: string;
  size?: string;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
}

@Injectable({ providedIn: 'root' })
export class ProductService {
  private firestore = inject(Firestore);
  private currentPage = 1;
  private pageSize = 8;
  private lastDoc: any = null;
  private firstDoc: any = null;
  private products$: Observable<Product[]> = of([]);
  private pageCount = 0;
  private totalCount = 0;
  private paginationInfo$ = new BehaviorSubject<PaginationInfo>({
    currentPage: 1,
    pageCount: 0,
    currentPageCount: 0,
    totalCount: 0,
    from: 0,
    to: 0
  });

  constructor() {
    this.currentPage = 1;
    this.firstDoc = null;
    this.lastDoc = null
    // this.getTotalProductsCount('');
    // this.loadFirstPage();
  }

  private loadFirstPage(filter: Filter, sort: 'price' | 'popularity' | 'newest' | null, searchText: string): void {
    this.products$ = this.getProducts('', filter, sort, searchText);
  }

  private async getTotalProductsCount(
    filter: Filter,
    searchText: string = ''
  ) {
    try {
      const constraints: any[] = [];
      // ---- SEARCH ----
      constraints.push(where('isAvailable', '==', true));
      if (searchText && searchText.trim() !== '') {

        // const s = searchText.toLowerCase();
        const s = searchText.toLowerCase()
        constraints.push(where('nameLower', '>=', s));
        constraints.push(where('nameLower', '<=', s + '\uf8ff'));
      }
      // ---- FILTERS ----
      if (filter.gender) constraints.push(where('gender', '==', filter.gender));
      if (filter.categories && filter.categories.length > 0) constraints.push(where('productCategory', 'in', filter.categories));
      if (filter.color) constraints.push(where('colors', 'array-contains', {
        name: filter.color.name,
        code: filter.color.code
      }));
      if (filter.themes && filter.themes.length > 0) constraints.push(where('designTheme', 'in', filter.themes));
      if (filter.sizes && filter.sizes.length > 0) constraints.push(where('sizes', 'in', filter.sizes));
      if (filter.price && filter.price > 0) constraints.push(where('price', '<=', filter.price));

      const q = query(collection(this.firestore, 'products'), ...constraints);

      const snapshot = await getCountFromServer(q);
      const count = snapshot.data().count || 0;
      this.totalCount = count;
      this.pageCount = Math.ceil(count / this.pageSize);
      this.updatePaginationInfo();

    } catch (error) {
      console.error('Error fetching total product count:', error);
    }
  }

  private updatePaginationInfo(currentPageCount?: number): void {
    const from = ((this.currentPage - 1) * this.pageSize) + 1;
    const to = Math.min(this.currentPage * this.pageSize, this.totalCount)

    this.paginationInfo$.next({
      currentPage: this.currentPage,
      pageCount: this.pageCount,
      currentPageCount: currentPageCount ?? this.paginationInfo$.value.currentPageCount,
      totalCount: this.totalCount,
      from,
      to
    })
  }

  getProductObservable(filter: Filter, sort: 'price' | 'newest' | null, searchText: string) {
    this.currentPage = 1;
    this.lastDoc = null;
    this.firstDoc = null;
    this.getTotalProductsCount(filter, searchText)
    this.loadFirstPage(filter, sort, searchText);
    return this.products$ as Observable<Product[]>
  }

  getPaginationInfoObservable() {
    return this.paginationInfo$ as Observable<PaginationInfo>
  }

  // searchByName(batchSize: number, name?: string): Observable<Product[]> {
  //   const stockRef = collection(this.firestore, 'stock');

  //   // Base conditions
  //   let conditions: any[] = [
  //     where('status', '==', 'Active'),
  //     where('quantity', '>', 0)
  //   ];

  //   // ---- SEARCH ----
  //   if (name?.trim()) {
  //     const s = name.trim().toLowerCase();   // always use lowercase
  //     conditions.push(where('nameLower', '>=', s));
  //     conditions.push(where('nameLower', '<=', s + '\uf8ff'));
  //   }

  //   // Build Firestore query
  //   const q = query(
  //     stockRef,
  //     ...conditions,
  //     // limit(batchSize)
  //   );

  //   return collectionData(q, { idField: 'id' }) as Observable<Product[]>;
  // }


  getProducts(
    type: 'next' | 'previous' | 'page' | '',
    filter: Filter,
    sort: 'price' | 'popularity' | 'newest' | null,
    searchText: string = ''
  ): Observable<Product[]> {

    let q: Query;

    // -------------------------------
    // SORTING
    // -------------------------------
    let orderField: keyof Product = 'createdAt';
    let orderDirection: any = 'desc';

    if (sort === 'price') {
      orderField = 'price';
      orderDirection = 'asc';

    } else if (sort === 'newest') {
      orderField = 'createdAt';
      orderDirection = 'desc';
    }

    // -------------------------------
    // BUILD QUERY
    // -------------------------------
    const buildQuery = (startSnap?: any, endSnap?: any, isPrev = false) => {
      const conditions: any[] = [];
      conditions.push(where('isAvailable', '==', true));
      // ---- SEARCH ----
      if (searchText.trim() !== '') {
        const s = searchText.trim().toLowerCase();   // always use lowercase
        conditions.push(where('nameLower', '>=', s));
        conditions.push(where('nameLower', '<=', s + '\uf8ff'));
      }

      // ---- FILTERS ----
      if (filter.gender) conditions.push(where('gender', '==', filter.gender));
      if (filter.categories && filter.categories.length > 0) conditions.push(where('productCategory', 'in', filter.categories));
      if (filter.color) conditions.push(where('colors', 'array-contains', {
        name: filter.color.name,
        code: filter.color.code
      }));
      if (filter.themes && filter.themes.length > 0) conditions.push(where('designTheme', 'in', filter.themes));
      if (filter.sizes && filter.sizes.length > 0) conditions.push(where('sizes', 'in', filter.sizes));
      if (filter.price && filter.price > 0) conditions.push(where('price', '<=', filter.price));


      // ---- ORDER ----
      conditions.push(orderBy(orderField as string, orderDirection));

      // ---- PAGINATION (correct way) ----
      if (startSnap) conditions.push(startAfter(startSnap));
      if (endSnap) conditions.push(endBefore(endSnap));

      conditions.push(isPrev ? limitToLast(this.pageSize) : limit(this.pageSize));

      return query(collection(this.firestore, 'products'), ...conditions);
    };

    // -------------------------------
    // PAGINATION LOGIC
    // -------------------------------
    if (type === 'next' && this.lastDoc) {
      q = buildQuery(this.lastDoc, null, false);
    } else if (type === 'previous' && this.firstDoc) {
      q = buildQuery(null, this.firstDoc, true);
    } else {
      q = buildQuery();
    }

    // -------------------------------
    // RUN QUERY AND UPDATE CURSORS
    // -------------------------------
    return from(getDocs(q)).pipe(
      map(snapshot => {
        const items = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as Product[];

        // Save document snapshots for next/prev
        if (snapshot.docs.length > 0) {
          this.firstDoc = snapshot.docs[0];                 // <-- snapshot, not field
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
        }

        this.updatePaginationInfo(items.length);
        return items;
      })
    );
  }


  firstPage(): Observable<Product[]> {
    this.currentPage = 1;
    const filter: Filter = {
      gender: null,
      categories: [],
      color: null,
      themes: [],
      sizes: [],
      price: 0
    }
    this.products$ = this.getProducts('', filter, null, '');
    return this.products$;
  }

  nextPage(filter: any, sort: any, searchText: string): Observable<Product[]> {
    if (this.currentPage < this.pageCount || this.pageCount === 0) {
      this.currentPage++;
      this.products$ = this.getProducts('next', filter, sort, searchText);
    }
    return this.products$;
  }

  previousPage(filter: any, sort: any, searchText: string): Observable<Product[]> {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.products$ = this.getProducts('previous', filter, sort, searchText);
    }
    return this.products$;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.pageCount && page !== this.currentPage) {
      this.currentPage = page;
      const filter: Filter = {
        gender: null,
        categories: [],
        color: null,
        themes: [],
        sizes: [],
        price: 0
      }
      // Note: For large datasets, you might need a more efficient way to jump to pages
      this.products$ = this.getProducts('page', filter, null, '');
      this.updatePaginationInfo();
    }
  }

  /** Featured products */
  // getFeaturedProducts(pageSize: number): Observable<Product[]> {
  //   const baseRef = collection(this.firestore, 'products');
  //   const q = query(baseRef, where('isFeaturedProduct', '==', true), limit(pageSize));
  //   return collectionData(q, { idField: 'id' }) as Observable<Product[]>;
  // }

  getFeaturedProducts(pageSize: number): Observable<Product[]> {
    const baseRef = collection(this.firestore, 'products');
    const q = query(
      baseRef,
      where('isFeaturedProduct', '==', true),
      where('isAvailable', '==', true),
      limit(pageSize)
    );
    return collectionData(q, { idField: 'id' }) as Observable<Product[]>;
  }

  /** Single product by id */
  getProduct(id: string): Observable<Product | undefined> {
    const productRef = doc(this.firestore, `products/${id}`);
    return docData(productRef, { idField: 'id' }) as Observable<Product | undefined>;
  }

  /** CRUD operations */
  addProduct(product: Product) {
    const productsRef = collection(this.firestore, 'products');
    return from(addDoc(productsRef, product));
  }

  updateProduct(id: string, product: Partial<Product>) {
    const productRef = doc(this.firestore, `products/${id}`);
    return from(updateDoc(productRef, product));
  }

  deleteProduct(id: string) {
    const productRef = doc(this.firestore, `products/${id}`);
    return from(deleteDoc(productRef));
  }
}
